<!--
  KpiCard.vue
  KPI指標カードコンポーネント
  
  Created: 2025-08-11
  Author: Claude
-->
<template>
  <div 
    class="kpi-card" 
    @click="$emit('click', kpi.id)"
    :class="{ loading: isLoading }"
  >
    <!-- カードヘッダー -->
    <div class="kpi-header">
      <div class="kpi-title">
        {{ kpi.title }}
        <!-- 定義アイコンを全KPIカードに表示 -->
        <span 
          class="definition-icon"
          @click.stop
          @mouseenter="showTooltip = true"
          @mouseleave="showTooltip = false"
          @touchstart.stop="toggleTooltip"
        >
          <i class="fas fa-info-circle"></i>
          <transition name="tooltip-fade">
            <div 
              v-if="showTooltip" 
              class="definition-tooltip"
              :class="tooltipPositionClass"
            >
              <div class="tooltip-header">
                <i :class="`fas fa-${kpi.icon}`"></i>
                {{ kpi.title }}の定義
              </div>
              <div class="tooltip-content">
                <!-- 集計期間 -->
                <div class="tooltip-section" v-if="definition.period">
                  <div class="section-title">
                    <i class="fas fa-clock"></i> {{ definition.period.label }}
                  </div>
                  <div class="section-content">
                    {{ definition.period.description }}
                  </div>
                </div>
                
                <!-- 集計対象 -->
                <div class="tooltip-section" v-if="definition.target">
                  <div class="section-title">
                    <i class="fas fa-filter"></i> {{ definition.target.label }}
                  </div>
                  <div class="section-content">
                    <div v-if="definition.target.includes && definition.target.includes.length > 0">
                      <strong>含まれるもの:</strong>
                      <ul>
                        <li v-for="(item, index) in definition.target.includes" :key="'inc-' + index">
                          {{ item }}
                        </li>
                      </ul>
                    </div>
                    <div v-if="definition.target.excludes && definition.target.excludes.length > 0">
                      <strong>除外されるもの:</strong>
                      <ul>
                        <li v-for="(item, index) in definition.target.excludes" :key="'exc-' + index">
                          {{ item }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <!-- 計算方法 -->
                <div class="tooltip-section" v-if="definition.calculation">
                  <div class="section-title">
                    <i class="fas fa-calculator"></i> {{ definition.calculation.label }}
                  </div>
                  <div class="section-content">
                    <div class="calculation-formula" v-if="definition.calculation.formula">
                      {{ definition.calculation.formula }}
                    </div>
                    <div v-if="definition.calculation.description">
                      {{ definition.calculation.description }}
                    </div>
                  </div>
                </div>
                
                <!-- 比較基準 -->
                <div class="tooltip-section" v-if="definition.comparison">
                  <div class="section-title">
                    <i class="fas fa-chart-line"></i> {{ definition.comparison.label }}
                  </div>
                  <div class="section-content">
                    {{ definition.comparison.description }}
                  </div>
                </div>
                
                <!-- 更新頻度 -->
                <div class="tooltip-footer" v-if="definition.updateFrequency">
                  <i class="fas fa-sync-alt"></i>
                  更新: {{ definition.updateFrequency }}
                </div>
              </div>
            </div>
          </transition>
        </span>
      </div>
      <div class="kpi-badge" :style="badgeStyle">
        <i :class="`fas fa-${kpi.icon}`"></i>
        {{ kpi.unit }}
      </div>
    </div>
    
    <!-- メイン数値 -->
    <div class="kpi-value">
      <template v-if="!isLoading">
        <!-- 本日のタスクカードの場合は総数を表示しない -->
        <template v-if="kpi.id !== 'todayTasks'">
          <span class="value-number">{{ formattedValue }}</span>
          <span class="value-unit">{{ kpi.unit }}</span>
          <span v-if="data.yearLabel" class="year-label">（{{ data.yearLabel }}）</span>
        </template>
        <!-- 本日の応募カードの場合、昨日の数も表示 -->
        <div v-if="kpi.id === 'todayApplications' && data && data.yesterdayCount !== undefined" class="yesterday-value">
          昨日: {{ data.yesterdayCount }}件
        </div>
        <!-- 総応募者数カードの場合、応募日無しの件数を表示 -->
        <div v-if="kpi.id === 'totalApplications' && data && data.noSubscribeDateCount > 0" class="no-subscribe-date-info">
          応募日無：{{ data.noSubscribeDateCount }}件
        </div>
      </template>
      <template v-else>
        <div class="skeleton-value"></div>
      </template>
    </div>
    
    <!-- ミニチャート（本日の応募、サマリー、タスク以外） -->
    <div v-if="!shouldHideChart" class="kpi-chart">
      <canvas :ref="`chart-${kpi.id}`" width="100%" height="60"></canvas>
    </div>
    
    <!-- フッター情報 -->
    <div class="kpi-footer">
      <!-- 総応募数の場合はタブ内訳を表示 -->
      <template v-if="kpi.id === 'totalApplications' && data && data.tabBreakdown && data.tabBreakdown.length > 0">
        <div class="tab-breakdown">
          <div class="breakdown-title">タブ別内訳:</div>
          <div class="breakdown-items">
            <div v-for="tab in data.tabBreakdown.slice(0, 3)" :key="tab.tab_id" class="breakdown-item">
              <span class="tab-name">{{ tab.tab_name }}:</span>
              <span class="tab-count">{{ tab.count }}件</span>
            </div>
            <div v-if="data.tabBreakdown.length > 3" class="breakdown-more">
              他{{ data.tabBreakdown.length - 3 }}タブ
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="data && data.trend">
        <div class="trend-indicator">
          <i :class="trendIcon"></i>
          <span :class="trendClass">{{ trendText }}</span>
          <span class="trend-label">{{ data.trend.label || '前期比' }}</span>
        </div>
      </template>
      <template v-else-if="data && data.progress">
        <div class="progress-indicator">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: data.progress.percentage + '%' }"></div>
          </div>
          <span class="progress-text">目標の{{ data.progress.percentage }}%</span>
        </div>
      </template>
      <template v-else-if="data && data.status">
        <div class="status-indicator">
          <i class="fas fa-check-circle"></i>
          <span>{{ data.status }}</span>
        </div>
      </template>
      <!-- サマリーカード用 -->
      <template v-else-if="data && data.displayType === 'summary'">
        <div class="summary-footer">
          <div class="summary-footer-item">
            <span class="label">総面接数:</span>
            <span class="value">{{ data.summaryData.totalInterviews.toLocaleString() }}件</span>
          </div>
          <div class="summary-footer-item">
            <span class="label">総採用数:</span>
            <span class="value">{{ (data.summaryData.totalHired || 0).toLocaleString() }}名</span>
          </div>
          <div class="summary-footer-item highlight">
            <span class="label">採用率:</span>
            <span class="value">{{ data.summaryData.conversionRate }}%</span>
          </div>
          <div class="summary-footer-item sub-info">
            <span class="label">今月採用:</span>
            <span class="value">{{ (data.summaryData.hiredThisMonth || 0).toLocaleString() }}名</span>
          </div>
        </div>
      </template>
      <!-- タスクカード用 -->
      <template v-else-if="data && data.displayType === 'tasks'">
        <div class="tasks-footer">
          <span class="task-item" v-if="data.taskData.newApplicants > 0">
            <i class="fas fa-star"></i>
            新着: {{ data.taskData.newApplicants }}件
          </span>
          <span class="footer-divider" v-if="data.taskData.newApplicants > 0">|</span>
          <span class="task-item" v-if="data.taskData.robotResponded > 0">
            <i class="fas fa-robot"></i>
            ロボ回答: {{ data.taskData.robotResponded }}件
          </span>
          <span class="footer-divider" v-if="data.taskData.robotResponded > 0 && data.taskData.messageReceived > 0">|</span>
          <span class="task-item" v-if="data.taskData.messageReceived > 0">
            <i class="fas fa-envelope"></i>
            メッセージ: {{ data.taskData.messageReceived }}件
          </span>
        </div>
      </template>
      <template v-else>
        <div class="no-data">
          <span>データなし</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';

export default {
  name: 'KpiCard',
  props: {
    kpi: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      default: () => ({})
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      chart: null,
      showTooltip: false,
      tooltipPosition: 'bottom',
      isMobile: false,
      kpiDefinitions: {}
    };
  },
  
  computed: {
    /**
     * チャートを非表示にするかどうか
     */
    shouldHideChart() {
      return this.kpi.id === 'todayApplications' || 
             this.kpi.id === 'summaryOverview' || 
             this.kpi.id === 'todayTasks';
    },
    
    /**
     * バッジのスタイル
     */
    badgeStyle() {
      return {
        backgroundColor: this.kpi.color + '20',
        color: this.kpi.color
      };
    },
    
    /**
     * フォーマット済み数値
     */
    formattedValue() {
      if (!this.data || this.data.value === undefined) return '0';
      
      const value = this.data.value;
      
      // 単位に応じたフォーマット
      if (this.kpi.unit === '%') {
        return value.toFixed(1) + '%';
      } else if (this.kpi.unit === '日') {
        return value.toFixed(1) + '日';
      } else if (this.kpi.unit === '倍') {
        return value.toFixed(2) + '倍';
      } else {
        // 通常の数値は3桁区切り
        return value.toLocaleString();
      }
    },
    
    /**
     * トレンドアイコン
     */
    trendIcon() {
      if (!this.data || !this.data.trend) return '';
      
      const direction = this.data.trend.direction;
      if (direction === 'up') {
        return 'fas fa-arrow-up';
      } else if (direction === 'down') {
        return 'fas fa-arrow-down';
      } else {
        return 'fas fa-minus';
      }
    },
    
    /**
     * トレンドクラス
     */
    trendClass() {
      if (!this.data || !this.data.trend) return '';
      
      const direction = this.data.trend.direction;
      if (direction === 'up') {
        return 'trend-up';
      } else if (direction === 'down') {
        return 'trend-down';
      } else {
        return 'trend-neutral';
      }
    },
    
    /**
     * トレンドテキスト
     */
    trendText() {
      if (!this.data || !this.data.trend) return '';
      
      const trend = this.data.trend;
      if (trend.percentage !== undefined) {
        return `${trend.direction === 'up' ? '+' : '-'}${Math.abs(trend.percentage)}%`;
      } else if (trend.value !== undefined) {
        return `${trend.direction === 'up' ? '+' : ''}${trend.value}`;
      }
      return '';
    },
    
    /**
     * KPI定義データの取得
     */
    definition() {
      return this.kpiDefinitions[this.kpi.id] || this.getDefaultDefinition();
    },
    
    /**
     * ツールチップの位置クラス
     */
    tooltipPositionClass() {
      return `tooltip-${this.tooltipPosition}`;
    }
  },
  
  created() {
    // 定義データのインポート
    import('../constants/kpiDefinitions.js').then(module => {
      this.kpiDefinitions = module.default;
    }).catch(error => {
      console.error('定義データの読み込みに失敗しました:', error);
      this.kpiDefinitions = {};
    });
  },
  
  mounted() {
    // デバッグ用：KPIカードがマウントされたことを確認
    console.log('KpiCard mounted:', this.kpi.id, this.kpi.title);
    if (this.kpi.id === 'conversionRate') {
      console.log('採用転換率カードが表示されています');
    }
    this.initChart();
    
    // 以下を追加
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    
    // 他のツールチップを閉じるイベントリスナー
    this.$root.$on('close-other-tooltips', (kpiId) => {
      if (kpiId !== this.kpi.id) {
        this.showTooltip = false;
      }
    });
  },
  
  watch: {
    'data.chartData': {
      handler(newData) {
        if (newData) {
          this.updateChart();
        }
      },
      deep: true
    },
    
    isLoading(newVal) {
      if (!newVal && this.data && this.data.chartData) {
        this.$nextTick(() => {
          this.updateChart();
        });
      }
    }
  },
  
  methods: {
    /**
     * ツールチップのヘッダーを取得
     */
    getTooltipHeader() {
      return `${this.kpi.title}の定義`;
    },
    
    /**
     * ツールチップの内容を取得
     */
    getTooltipContent() {
      const definitions = {
        todayApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            本日 0:00～23:59 のデータ
          </div>
          <div class="definition-item">
            <strong>前期比：</strong><br/>
            昨日 0:00～23:59 との比較
          </div>
        `,
        weeklyApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            今日から遡って7日間
          </div>
          <div class="definition-item">
            <strong>前期比：</strong><br/>
            その前の7日間との比較
          </div>
        `,
        monthlyApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            月初から今日まで
          </div>
          <div class="definition-item">
            <strong>前期比：</strong><br/>
            前月の同期間との比較
          </div>
        `,
        currentMonthApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            月初から今日まで
          </div>
          <div class="definition-item">
            <strong>前期比：</strong><br/>
            前月の同期間との比較
          </div>
        `,
        lastMonthApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            先月の月初～月末まで
          </div>
          <div class="definition-item">
            <strong>前期比：</strong><br/>
            前々月全体との比較
          </div>
        `,
        todayInterviews: `
          <div class="definition-item">
            <strong>集計対象：</strong><br/>
            本日予定の面接件数
          </div>
        `,
        totalApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            全期間の有効な応募者数
          </div>
          <div class="definition-item">
            <strong>内訳表示：</strong><br/>
            ・媒体別（Indeed、マイナビ等）<br/>
            ・タブ別の応募者数
          </div>
        `,
        conversionRate: `
          <div class="definition-item">
            <strong>計算式：</strong><br/>
            採用者数 ÷ 総応募者数 × 100
          </div>
          <div class="definition-item">
            <strong>採用者の定義：</strong><br/>
            採用、入社済み、内定承諾、<br/>
            採用決定ステージの応募者
          </div>
        `,
        averageProcessTime: `
          <div class="definition-item">
            <strong>集計内容：</strong><br/>
            応募から採用までの平均日数
          </div>
        `,
        currentMonthHired: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            当月の月初から今日まで
          </div>
          <div class="definition-item">
            <strong>採用者の定義：</strong><br/>
            採用、入社済み、内定承諾、<br/>
            採用決定ステージの応募者
          </div>
        `,
        currentYearApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            今年度（4月1日～現在）
          </div>
          <div class="definition-item">
            <strong>前期比：</strong><br/>
            前年度同期間との比較
          </div>
        `,
        previousYearApplications: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            前年度（4月1日～3月31日）
          </div>
          <div class="definition-item">
            <strong>備考：</strong><br/>
            完全な1年間のデータ
          </div>
        `,
        currentYearInterviews: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            今年度（4月1日～現在）
          </div>
          <div class="definition-item">
            <strong>データ元：</strong><br/>
            面接履歴データから集計
          </div>
        `,
        previousYearInterviews: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            前年度（4月1日～3月31日）
          </div>
          <div class="definition-item">
            <strong>データ元：</strong><br/>
            面接履歴データから集計
          </div>
        `,
        currentYearHired: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            今年度（4月1日～現在）
          </div>
          <div class="definition-item">
            <strong>採用者の定義：</strong><br/>
            採用関連ステージの応募者
          </div>
        `,
        previousYearHired: `
          <div class="definition-item">
            <strong>集計期間：</strong><br/>
            前年度（4月1日～3月31日）
          </div>
          <div class="definition-item">
            <strong>採用者の定義：</strong><br/>
            採用関連ステージの応募者
          </div>
        `
      };
      
      return definitions[this.kpi.id] || `
        <div class="definition-item">
          集計期間・条件の詳細
        </div>
      `;
    },
    
    /**
     * チャートの初期化
     */
    initChart() {
      // チャートを表示しないカードの場合はスキップ
      if (this.shouldHideChart) return;
      
      if (this.isLoading || !this.data || !this.data.chartData) return;
      
      const canvas = this.$refs[`chart-${this.kpi.id}`];
      if (!canvas || !canvas.getContext) {
        console.warn(`Canvas not available for KPI: ${this.kpi.id}`);
        return;
      }
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn(`Unable to get 2D context for KPI: ${this.kpi.id}`);
        return;
      }
      
      // チャートの設定
      const labels = this.generateLabels();
      const chartData = (this.data && this.data.chartData && this.data.chartData.length > 0) 
        ? this.data.chartData 
        : Array(labels.length).fill(0); // データがない場合は0で埋める
      
      const config = {
        type: this.kpi.chartType || 'line',
        data: {
          labels: labels,
          datasets: [{
            label: '',  // 空文字列を設定（undefinedを防ぐ）
            data: chartData,
            borderColor: this.kpi.color,
            backgroundColor: this.kpi.chartType === 'bar' ? 
              this.kpi.color : 
              this.kpi.color + '20',
            fill: this.kpi.chartType === 'line',
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 0,
            borderRadius: this.kpi.chartType === 'bar' ? 4 : 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false  // 凡例だけを非表示
          },
          tooltips: {
            enabled: false  // ツールチップは非表示
          },
          elements: {
            point: {
              radius: 0  // データポイントの点は非表示
            }
          },
          scales: {
            xAxes: [{
              display: true,  // X軸は表示（ラベルを見せるため）
              gridLines: {
                display: false  // グリッド線は非表示
              },
              ticks: {
                fontSize: 10,
                fontColor: '#999'
              }
            }],
            yAxes: [{
              display: false,  // Y軸は非表示（数値軸は不要）
              gridLines: {
                display: false
              }
            }]
          }
        }
      };
      
      this.chart = new Chart(ctx, config);
    },
    
    /**
     * チャートの更新
     */
    updateChart() {
      // チャートを表示しないカードの場合はスキップ
      if (this.shouldHideChart) return;
      
      if (!this.chart) {
        this.initChart();
        return;
      }
      
      if (this.data && this.data.chartData) {
        this.chart.data.labels = this.generateLabels();
        this.chart.data.datasets[0].data = this.data.chartData;
        this.chart.data.datasets[0].label = '';  // 凡例ラベルを空文字列に
        this.chart.update('none'); // アニメーションなしで更新
      }
    },
    
    /**
     * ラベルの生成
     */
    generateLabels() {
      // data.labelsが明示的に設定されている場合はそれを使用
      if (this.data && this.data.labels && this.data.labels.length > 0) {
        return this.data.labels;
      }
      
      // データがない場合でもデフォルトのラベルを返す
      const length = (this.data && this.data.chartData && this.data.chartData.length) || 7;
      
      // KPIタイプに応じたラベル生成
      switch(this.kpi.id) {
        case 'weeklyApplications':
          // 週間応募数用のラベル（labelsが設定されていない場合のフォールバック）
          if (this.data && this.data.labels) {
            return this.data.labels;
          }
          return ['月', '火', '水', '木', '金', '土', '日'];
          
        case 'todayInterviews':
          return ['8時', '9時', '10時', '11時', '12時'];
          
        case 'monthlyApplications':
        case 'lastMonthApplications':
        case 'currentMonthApplications':
        case 'currentMonthInterviews':
        case 'lastMonthInterviews':
          // 週単位のラベル
          if (this.data && this.data.chartData && this.data.chartData.length > 0) {
            // データ数に応じて週ラベルを生成
            return this.data.chartData.map((_, index) => `${index + 1}w`);
          }
          return ['1w', '2w', '3w', '4w'];
          
        case 'todayApplications':
          // 時間帯別
          return ['0-4時', '4-8時', '8-12時', '12-16時', '16-20時', '20-24時'];
          
        case 'totalApplications':
        case 'conversionRate':
          // 月別トレンド
          return ['7月', '8月', '9月', '10月', '11月', '12月', '1月'];
          
        // 年度別カードの追加
        case 'currentYearApplications':
        case 'previousYearApplications':
        case 'currentYearInterviews':
        case 'previousYearInterviews':
        case 'currentYearHired':
        case 'previousYearHired':
          return this.generateFiscalYearLabels();
          
        // 採用数推移の追加
        case 'hiredTrend':
          return this.generateMonthlyTrendLabels();
          
        default:
          // より適切なデフォルトラベル
          return this.generateDefaultLabels(length);
      }
    },
    
    /**
     * 年度別ラベル生成（4月始まり）
     * @returns {Array<string>} 月ラベルの配列
     */
    generateFiscalYearLabels() {
      const fiscalMonths = ['4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月', '1月', '2月', '3月'];
      
      // データがある場合は、その長さに応じてラベルを調整
      if (this.data && this.data.chartData && this.data.chartData.length > 0) {
        const dataLength = this.data.chartData.length;
        
        // 今年度の場合：4月から現在月まで
        if (this.kpi.id.includes('currentYear')) {
          return fiscalMonths.slice(0, dataLength);
        }
        
        // 前年度の場合：常に12ヶ月分
        return fiscalMonths;
      }
      
      // データがない場合のフォールバック
      return fiscalMonths;
    },

    /**
     * 月別トレンドラベル生成（直近12ヶ月）
     * @returns {Array<string>} 月ラベルの配列
     */
    generateMonthlyTrendLabels() {
      // データにラベルが含まれている場合はそれを優先
      if (this.data && this.data.labels && this.data.labels.length > 0) {
        return this.data.labels;
      }
      
      // データから月数を取得
      const dataLength = (this.data && this.data.chartData && this.data.chartData.length) || 12;
      
      // 現在月から遡って表示
      const currentDate = new Date();
      const labels = [];
      
      for (let i = dataLength - 1; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setMonth(date.getMonth() - i);
        labels.push(`${date.getMonth() + 1}月`);
      }
      
      return labels;
    },

    /**
     * デフォルトラベル生成（改善版）
     * @param {number} length - ラベル数
     * @returns {Array<string>} デフォルトラベルの配列
     */
    generateDefaultLabels(length) {
      // より意味のあるデフォルトラベル
      const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
      
      if (length <= 12) {
        return months.slice(0, length);
      }
      
      // 12を超える場合は番号付きラベル
      return Array.from({length}, (_, i) => `${i + 1}`);
    },
    
    /**
     * モバイルデバイスかどうかの判定
     */
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    
    /**
     * ツールチップの表示切り替え（モバイル用）
     */
    toggleTooltip() {
      if (this.isMobile) {
        this.showTooltip = !this.showTooltip;
        if (this.showTooltip) {
          // 他のツールチップを閉じる
          this.$root.$emit('close-other-tooltips', this.kpi.id);
        }
      }
    },
    
    /**
     * デフォルトの定義データ
     */
    getDefaultDefinition() {
      return {
        period: {
          label: '集計期間',
          description: '指定された期間'
        },
        target: {
          label: '集計対象',
          includes: ['すべてのデータ'],
          excludes: []
        },
        updateFrequency: 'リアルタイム（30秒ごと）'
      };
    }
  },
  
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    // 以下を追加
    window.removeEventListener('resize', this.checkMobile);
    this.$root.$off('close-other-tooltips');
  }
}
</script>

<style lang="scss" scoped>
.kpi-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }
  
  &.loading {
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.4),
        transparent
      );
      animation: shimmer 2s infinite;
    }
  }
}

.kpi-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.kpi-title {
  font-size: 13px;
  color: #666;
  font-weight: 500;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

/* 定義アイコンのスタイル */
.definition-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  color: #999;
  cursor: help;
  font-size: 12px;
  transition: color 0.2s;
  
  &:hover {
    color: #666;
  }
  
  i {
    font-size: 12px;
  }
}

/* ツールチップのスタイル */
.definition-tooltip {
  position: absolute;
  z-index: 1000;
  width: 320px;
  max-width: calc(100vw - 40px);
  background: #2c3e50;
  color: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  font-size: 13px;
  line-height: 1.6;
  pointer-events: auto;
  
  // デフォルトは下向き
  &.tooltip-bottom {
    top: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid #2c3e50;
    }
  }
}

.tooltip-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  font-size: 14px;
  
  i {
    margin-right: 8px;
    color: #3498db;
  }
}

.tooltip-content {
  padding: 16px;
  
  .definition-detail {
    strong {
      color: #3498db;
    }
    
    ul {
      margin: 8px 0 0 20px;
      padding: 0;
      
      li {
        margin: 4px 0;
        list-style-type: disc;
      }
    }
  }
  
  .definition-item {
    margin-bottom: 10px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    strong {
      color: #3498db;
      font-size: 11px;
    }
  }
}

.tooltip-section {
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #3498db;
  font-size: 13px;
  
  i {
    margin-right: 6px;
    font-size: 12px;
  }
}

.section-content {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  
  strong {
    display: block;
    margin-top: 8px;
    margin-bottom: 4px;
    color: #fff;
  }
  
  ul {
    margin: 4px 0 0 20px;
    padding: 0;
    
    li {
      margin: 2px 0;
      list-style-type: disc;
    }
  }
}

.calculation-formula {
  background: rgba(52, 152, 219, 0.2);
  padding: 8px 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  margin-bottom: 8px;
  text-align: center;
}

.tooltip-footer {
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  
  i {
    margin-right: 6px;
  }
}

/* トランジション */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* モバイル対応 */
@media (max-width: 767px) {
  .definition-tooltip {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    max-height: 80vh;
    overflow-y: auto;
    
    &::before {
      display: none;
    }
  }
}

.kpi-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  
  i {
    font-size: 10px;
  }
}

.kpi-value {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 16px;
  line-height: 1;
  
  .value-number {
    // 数値部分のスタイル
  }
  
  .value-unit {
    font-size: 16px;
    font-weight: 400;
    color: #7f8c8d;
    margin-left: 4px;
  }
  
  .year-label {
    font-size: 14px;
    color: #95a5a6;
    font-weight: normal;
    margin-left: 8px;
  }
  
  .skeleton-value {
    width: 120px;
    height: 32px;
    background: #f0f0f0;
    border-radius: 4px;
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  .yesterday-value {
    font-size: 14px;
    font-weight: 400;
    color: #7f8c8d;
    margin-top: 8px;
  }
  
  .no-subscribe-date-info {
    font-size: 12px;
    color: #e74c3c;
    margin-top: 4px;
    font-weight: 500;
  }
}

.kpi-chart {
  height: 60px;
  margin-bottom: 12px;
  position: relative;
}

.kpi-footer {
  min-height: 24px;
}

.trend-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  
  i {
    font-size: 11px;
  }
  
  .trend-up {
    color: #2ecc71;
    font-weight: 600;
  }
  
  .trend-down {
    color: #e74c3c;
    font-weight: 600;
  }
  
  .trend-neutral {
    color: #95a5a6;
    font-weight: 600;
  }
  
  .trend-label {
    color: #888;
    margin-left: 4px;
  }
}

.progress-indicator {
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  .progress-bar {
    height: 6px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #3498db, #2980b9);
      border-radius: 3px;
      transition: width 0.5s ease;
    }
  }
  
  .progress-text {
    font-size: 11px;
    color: #666;
  }
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #2ecc71;
  
  i {
    font-size: 11px;
  }
}

.no-data {
  font-size: 12px;
  color: #999;
}

// 新規追加：サマリーフッター
.summary-footer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  
  .summary-footer-item {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    
    .label {
      color: #666;
    }
    
    .value {
      font-weight: 600;
      color: #2c3e50;
    }
    
    &.highlight {
      background: #e3f2fd;
      padding: 6px 8px;
      border-radius: 4px;
      margin: 4px 0;
      
      .value {
        color: #1976d2;
        font-size: 14px;
      }
    }
    
    &.sub-info {
      font-size: 11px;
      opacity: 0.8;
    }
  }
}

// 新規追加：タスクフッター
.tasks-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px; // より大きく
  flex-wrap: wrap;
  margin-top: 20px; // 上部に余白を追加
  
  .task-item {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #2c3e50; // より濃い色
    white-space: nowrap;
    font-weight: 600; // 太字
    padding: 8px 12px; // 背景用のパディング
    background: #f8f9fa; // 薄い背景色
    border-radius: 6px; // 角丸
    
    i {
      font-size: 12px;
      color: #3498db;
    }
  }
  
  .footer-divider {
    display: none; // 区切り線を非表示
  }
}

.tab-breakdown {
  font-size: 11px;
  
  .breakdown-title {
    color: #666;
    margin-bottom: 4px;
    font-weight: 600;
  }
  
  .breakdown-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  
  .breakdown-item {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
    
    .tab-name {
      color: #888;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 120px;
    }
    
    .tab-count {
      color: #2c3e50;
      font-weight: 600;
      margin-left: 8px;
    }
  }
  
  .breakdown-more {
    color: #999;
    font-style: italic;
    padding-top: 2px;
  }
}

// アニメーション
@keyframes shimmer {
  to {
    left: 100%;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

// レスポンシブ
@media (max-width: 768px) {
  .kpi-value {
    font-size: 28px;
  }
}

@media (max-width: 576px) {
  .kpi-card {
    padding: 15px;
  }
  
  .kpi-value {
    font-size: 24px;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/components/KpiCard.vue